<template>
  <v-container fluid>
    <v-card outlined>
      <v-toolbar dense color="primary" dark>
        <v-toolbar-title>Quotations</v-toolbar-title>
        <v-spacer />
        <v-btn small text :to="'/quotations/create'">
          <v-icon left>mdi-plus</v-icon> New
        </v-btn>
      </v-toolbar>

      <v-card-text>
        <v-text-field v-model="search" label="Search" prepend-inner-icon="mdi-magnify" clearable dense outlined
          hide-details class="mb-3" />
        <v-data-table :footer-props="{ itemsPerPageOptions: [10, 25, 50, 10000] }" :headers="headers" :items="items"
          :loading="loading" :options.sync="options" :server-items-length="total" dense>
          <!-- STATUS -->
          <template v-slot:item.is_locked="{ item }">
            <v-chip :color="item.is_locked ? 'red' : 'green'" dark small>
              {{ item.is_locked ? 'Locked' : 'Open' }}
            </v-chip>
          </template>
          <template v-slot:item.customer="{ value }"><small>({{ value.id }})</small> {{ value.name }}</template>
          <template v-slot:item.actual_date="{ value }">{{ formatDateUS(new Date(value)) }}</template>
          <!-- TOTAL_AMOUNT -->
          <template v-slot:item.items_total_amount="{ value }">
            {{ currencyFormat(value) }}
          </template>
          <template v-slot:item.project_code="{ value }">{{ value?.code || '-' }}</template>

          <!-- ACTIONS -->
          <template v-slot:item.actions="{ item }">
            <!-- PRINT -->
            <v-btn color="success" icon small @click="printPdf(item)">
              <v-icon>mdi-printer</v-icon>
            </v-btn>
            <!-- VIEW -->
            <v-btn color="purple" icon small @click="view(item.id)">
              <v-icon>mdi-eye</v-icon>
            </v-btn>

            <!-- EDIT -->
            <v-btn color="yellow darken-3" icon small :disabled="item.is_locked" @click="edit(item.id)">
              <v-icon>mdi-pencil</v-icon>
            </v-btn>

            <!-- LOCK / UNLOCK -->
            <v-btn :color="item.is_locked ? 'green' : 'red'" icon small
              @click="item.is_locked ? unlock(item) : lock(item)">
              <v-icon>
                {{ item.is_locked ? 'mdi-lock-open' : 'mdi-lock' }}
              </v-icon>
            </v-btn>

            <!-- DELETE -->
            <v-btn :disabled="item.is_locked" color="red" icon small @click="destroy(item)">
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
// @ts-nocheck
import appAxios from '@/libs/app-axios'
import { currencyFormat, formatDateUS, confirmPrompt, ErrorPopup, SuccessPopup, loadPdf, LoadingPopup } from '@/libs/utils'
import { isAxiosError } from 'axios'
import debounce from 'lodash/debounce'

export default {
  data() {
    return {
      items: [],
      loading: false,
      total: 0,

      options: {
        page: 1,
        itemsPerPage: 25,
      },

      search: '',
      lastSearch: '',

      headers: [
        { text: 'Subject', value: 'subject' },
        { text: 'Project Code', value: 'project_code', align: 'center' },
        { text: 'Customer', value: 'customer' },
        { text: 'Date', value: 'actual_date', align: 'center' },
        { text: 'Status', value: 'is_locked', align: 'center' },
        { text: 'Total Amount', value: 'items_total_amount', align: 'right' },
        { text: 'Actions', value: 'actions', sortable: false, align: 'center' },
      ],
    }
  },
  watch: {
    options: {
      handler() {
        this.fetchData()
      },
      deep: true,
    },
    search: {
      handler: debounce(function (val) {
        const trimmed = (val || '').trim()

        if (trimmed === this.lastSearch) return

        this.lastSearch = trimmed
        this.options.page = 1 // reset to first page
        this.fetchData()
      }, 500),
    },
  },
  methods: {
    async fetchData() {
      try {
        this.loading = true
        const { page, itemsPerPage } = this.options
        const res = await appAxios().get('/v3/quotations', {
          params: {
            page,
            limit: itemsPerPage,
            search: this.search,
          },
        })
        this.items = res.data.data || []
        this.total = res.data.meta?.total || 0
      } catch (e) {
        console.error(e)
      } finally {
        this.loading = false
      }
    },
    view(id) {
      this.$router.push(`/quotations/${id}/show`)
    },
    edit(id) {
      this.$router.push(`/quotations/${id}/edit`)
    },
    async lock(item) {
      const ok = confirmPrompt(
        'LOCK QUOTATION',
        'Type "LOCK QUOTATION" to lock this quotation.\nThis cannot be undone.'
      )

      if (!ok) return

      try {
        const res = await appAxios().post(`/v3/quotations/${item.id}/lock`)
        item.is_locked = true
        SuccessPopup.fire('Success', res.data)
      } catch (e) {
        ErrorPopup.fire('Error', e.response?.data || 'Failed to lock')
      }
    },

    async printPdf(item) {
      try {
        LoadingPopup.fire('Loading', 'Generating PDF...')
        const { data } = await appAxios().get(`/v3/quotations/${item.id}/pdf`, {
          responseType: 'blob',
        })
        const blob = new Blob([data], { type: 'application/pdf' })
        loadPdf(blob)
        LoadingPopup.close()
      } catch (e) {
        if (isAxiosError(e)) {
          ErrorPopup.fire('Error', e.response?.data?.message || 'Failed to load PDF')
          return
        }
        if (e instanceof Error) {
          ErrorPopup.fire('Error', e.message)
        }
      }
    },

    async unlock(item) {
      const ok = confirmPrompt(
        'UNLOCK QUOTATION',
        'Type "UNLOCK QUOTATION" to unlock this quotation.'
      )

      if (!ok) return

      try {
        const res = await appAxios().post(`/v3/quotations/${item.id}/unlock`)
        item.is_locked = false
        SuccessPopup.fire('Success', res.data)
      } catch (e) {
        ErrorPopup.fire('Error', e.response?.data || 'Failed to unlock')
      }
    },

    async destroy(item) {
      const ok = confirmPrompt(
        'DELETE QUOTATION',
        'Type "DELETE QUOTATION" to permanently delete this quotation.'
      )

      if (!ok) return

      try {
        const res = await appAxios().delete(`/v3/quotations/${item.id}`)

        // remove from table
        this.items = this.items.filter(i => i.id !== item.id)

        SuccessPopup.fire('Success', res.data)
      } catch (e) {
        ErrorPopup.fire('Error', e.response?.data || 'Failed to delete')
      }
    },
    currencyFormat,
    formatDateUS,
  },
}
</script>
